import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CoreProvider, I18nConfig, LoadingScreen, queryConfig } from '@nohara-dxdev/react-core';
import {
  CRUD_ACTIONS,
  EntityProvider,
  OidcConfig,
  OidcProvider,
  ScopeProvider,
} from '@nohara-dxdev/react-oidc';
import { ThemeProvider } from '@nohara-dxdev/react-theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { MainErrorFallback } from '@/components/errors/main';
import { env } from '@/config/env';
import { registerFeatureDisabledHandler } from '@/features/feature-flags/utils';
import enLocale from '@/locales/en.json';
import jaLocale from '@/locales/ja.json';

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  );

  React.useEffect(() => {
    registerFeatureDisabledHandler(() => {
      void queryClient.invalidateQueries({ queryKey: ['feature-flags', 'me'] });
    });
  }, [queryClient]);

  const [oidcConfig] = React.useState<OidcConfig>(() => ({
    apiEndpoint: env.HOME_API_ENDPOINT,
    oauthEndpoint: env.OAUTH_DOMAIN,
    userPoolId: env.USER_POOL_ID,
    userPoolWebClientId: env.USER_POOL_WEB_CLIENT_ID,
    oidcCallbackPath: env.OIDC_CALLBACK_PATH,
    signOutPath: env.SIGN_OUT_PATH,
    permissionDefinitions: {
      project: CRUD_ACTIONS,
      building: CRUD_ACTIONS,
      item: CRUD_ACTIONS,
      itemTemplate: CRUD_ACTIONS,
      itemAttribute: CRUD_ACTIONS,
      itemAttributeValue: CRUD_ACTIONS,
      comment: CRUD_ACTIONS,
      commentResponse: CRUD_ACTIONS,
      floorPlan: CRUD_ACTIONS,
      roomPlan: CRUD_ACTIONS,
      task: CRUD_ACTIONS,
      report: CRUD_ACTIONS,
      settings: CRUD_ACTIONS,
      activity: ['read'],
    },
  }));

  const i18nConfig = React.useMemo(
    (): I18nConfig => ({
      resources: {
        en: {
          translation: enLocale,
        },
        ja: {
          translation: jaLocale,
        },
      },
      defaultNS: 'translation',
      fallbackNS: 'translation',
      fallbackLng: 'ja',
      lng: 'ja',
    }),
    [],
  );

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <CoreProvider i18nConfig={i18nConfig}>
        <ErrorBoundary FallbackComponent={MainErrorFallback}>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV && <ReactQueryDevtools />}
            <ThemeProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <OidcProvider config={oidcConfig}>
                  <EntityProvider>
                    <ScopeProvider>{children}</ScopeProvider>
                  </EntityProvider>
                </OidcProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </CoreProvider>
    </React.Suspense>
  );
};
